import PortalIdParser from 'PortalIdParser';
import { stringify } from 'hub-http/helpers/params';
import { buildUrl, parseUrl } from 'hub-http/helpers/url';
import { getPublicSiteDomain } from './publicPageUrls';
import { isWordPress } from 'hubspot-plugin-common';
import { getFullUrl } from 'hubspot-url-utils';
import { getSavedQueryParamString } from './queryParamUtils';
import { AllRoutes } from '../constants/PricingPageRoutes';
import * as Languages from '../constants/Languages';
import { getI18nLanguage } from './languageUtils';

const langIsInMap = (lang, langMap) => {
  return Object.keys(langMap).includes(lang);
};

export const langToUrl = langMap => {
  const lang = getI18nLanguage();

  if (!langIsInMap(lang, langMap)) {
    return langMap.en;
  }

  return langMap[lang];
};

const compact = arr => arr.filter(Boolean);

const _createInternalUrl = (path = '', query) => {
  const queryString = query && compact(Object.values(query || {})).length ? `?${stringify(query)}` : '';
  const baseUrl = `/${path}`;
  return `${baseUrl}${queryString}`;
};

const _createExternalUrl = (path = '', query) => {
  const {
    protocol,
    hostname
  } = parseUrl(getFullUrl('app'));
  const urlOptions = {
    protocol,
    hostname,
    path,
    query: stringify(query)
  };
  return buildUrl(urlOptions);
};

export const navigateTo = url => {
  if (isWordPress) {
    window.open(url, '_top');
  } else {
    window.location.href = url;
  }
};
export const tabsNavigateTo = ({
  path,
  history
}) => {
  const stringifiedParams = getSavedQueryParamString(['upgradeSource', 'show-tts-chat']);
  const pathWithSavedParams = stringifiedParams ? `${path}?${stringifiedParams}` : path;
  history.push(pathWithSavedParams);
  window.scrollTo(0, 0);
};
export const handleRedirect = (url, delay = 200) => {
  setTimeout(() => {
    navigateTo(url);
  }, delay);
};
export const getRootUrl = () => _createInternalUrl();
export const getNotFoundUrl = () => _createInternalUrl('/not-found');
export const getPricingPageUrl = (primaryNavId, query) => _createInternalUrl(`${primaryNavId}`, query);
export const getPricingAndFeaturesPageUrl = query => _createExternalUrl(`/browse/${PortalIdParser.get()}`, query);
export const getProductTabUrl = (productsAndPlansNavId, query) => _createInternalUrl(`${productsAndPlansNavId}`, query);

const getLegalSiteDomain = () => {
  const langMap = {
    [Languages.EN]: 'https://legal.hubspot.com',
    [Languages.DE]: 'https://legal.hubspot.com/de',
    [Languages.ES]: 'https://legal.hubspot.com/es',
    [Languages.PT]: 'https://legal.hubspot.com/br',
    [Languages.FR]: 'https://legal.hubspot.com/fr',
    [Languages.JA]: 'https://legal.hubspot.com/jp'
  };
  return langToUrl(langMap);
};

export const getSuiteLegalDisclaimerUrl = () => {
  const legalSiteDomain = getLegalSiteDomain();
  return `${legalSiteDomain}/crm-suite-bundle-pricing`;
};
export const getProductsAndServicesCatalogUrl = (section = '') => {
  const legalSiteDomain = getLegalSiteDomain();
  return `${legalSiteDomain}/hubspot-product-and-services-catalog${section}`;
};
export const getLegalStuffSiteDomain = () => {
  const legalSiteDomain = getLegalSiteDomain();
  return `${legalSiteDomain}/legal-stuff`;
};
export const getPrivacyPolicySiteDomain = () => {
  const legalSiteDomain = getLegalSiteDomain();
  return `${legalSiteDomain}/privacy-policy`;
};
export const getContactUsUrl = () => {
  const langMap = {
    [Languages.EN]: 'https://hubspot.com/company/contact',
    [Languages.DE]: 'https://hubspot.de/company/contact',
    [Languages.ES]: 'https://hubspot.es/company/contact',
    [Languages.PT]: 'https://br.hubspot.com/company/contact',
    [Languages.FR]: 'https://hubspot.fr/company/contact',
    [Languages.JA]: 'https://hubspot.jp/company/contact'
  };
  return langToUrl(langMap);
};
export const getHubSpotHomeUrl = () => {
  const langMap = {
    [Languages.EN]: 'https://hubspot.com',
    [Languages.DE]: 'https://hubspot.de',
    [Languages.ES]: 'https://hubspot.es',
    [Languages.PT]: 'https://br.hubspot.com',
    [Languages.FR]: 'https://hubspot.fr',
    [Languages.JA]: 'https://hubspot.jp'
  };
  return langToUrl(langMap);
}; // TODO: update this when we get the desired url

export const getLatamEsPromotionUrl = () => {
  const legalSiteDomain = getLegalSiteDomain();
  return `${legalSiteDomain}/latam-pricing-promotions`;
};
export const getHSFSPurchaseUrl = () => _createExternalUrl(`/hsfx-programs/${PortalIdParser.get()}/application/finish`);
export const getHSFSApplicationURL = () => _createExternalUrl(`/hsfx-programs/${PortalIdParser.get()}/application/company`);
export const getCommunityUrl = () => 'https://community.hubspot.com/';
export const getPaidUserManagementUrl = () => _createExternalUrl(`/settings/${PortalIdParser.get()}/users/paid-users`);
export const getActiveQuoteUrl = quoteId => _createExternalUrl(`/quote-checkout/${PortalIdParser.get()}/quotes/${quoteId}`); // TODO can remove this function after 4/7

export const getCmsRootUrl = hasThemedContentCreatorAccess => {
  if (hasThemedContentCreatorAccess) {
    return _createExternalUrl(`/website/${PortalIdParser.get()}/pages/site?signupSrc=trialPricingPage`);
  }

  return _createExternalUrl(`/marketplace/${PortalIdParser.get()}/products?childType=LANDING_PAGE&childType=SITE_PAGE&subType=COS_TEMPLATE_PACK`);
};
export const getInAppPaymentsUrl = () => _createExternalUrl(`/settings/${PortalIdParser.get()}/sales/payments`);
export const getInAppPaymentsKYCUrl = kyc => _createExternalUrl(`/settings/${PortalIdParser.get()}/sales/payments?kyc=${kyc}`);
export const getCmsHubRootUrl = () => _createExternalUrl(`/website/${PortalIdParser.get()}/splash`);
export const getGettingStartedPageUrl = () => _createExternalUrl(`/getting-started/${PortalIdParser.get()}`);
export const getInboundPricingFaqUrl = () => `${getPublicSiteDomain()}/inbound19-pricing-faq`; // TODO JS: Create this Type

export const getDTTUrl = (trialId, upgradeSource) => _createExternalUrl(`/signup/trial-signup?intent=trial&trialId=${trialId}&dtt_source=${upgradeSource}-pricing-page`); // We should make this use a langMap for the global launch

export const MC_LANDING_PAGE_URL = 'https://www.hubspot.com/marketing-contacts/anz';
export const getMCLandingPageUrl = () => {
  const langMap = {
    [Languages.EN]: 'https://hubspot.com/marketing-contacts',
    [Languages.DE]: 'https://hubspot.de/marketing-contacts',
    [Languages.ES]: 'https://hubspot.es/marketing-contacts',
    [Languages.PT]: 'https://br.hubspot.com/marketing-contacts',
    [Languages.FR]: 'https://hubspot.fr/marketing-contacts',
    [Languages.JA]: 'https://hubspot.jp/marketing-contacts'
  };
  return langToUrl(langMap);
};
export const getMarketplaceUrl = ({
  slug,
  isAppAuth
}) => {
  if (isAppAuth) {
    return `${getFullUrl('app')}/ecosystem/${PortalIdParser.get()}/marketplace/apps${slug}`;
  }

  const langMap = {
    [Languages.EN]: `https://ecosystem.hubspot.com/marketplace/apps${slug}`,
    [Languages.DE]: `https://ecosystem.hubspot.com/de/marketplace/apps${slug}`,
    [Languages.ES]: `https://ecosystem.hubspot.com/es/marketplace/apps${slug}`,
    [Languages.PT]: `https://ecosystem.hubspot.com/pt/marketplace/apps${slug}`,
    [Languages.FR]: `https://ecosystem.hubspot.com/fr/marketplace/apps${slug}`,
    [Languages.JA]: `https://ecosystem.hubspot.com/ja/marketplace/apps${slug}`
  };
  return langToUrl(langMap);
};
export const getAbsolutePricingPageUrl = ({
  slug,
  isAppAuth,
  isQA = false,
  level = ''
}) => {
  if (isAppAuth) {
    return `${getFullUrl('app')}/pricing/${PortalIdParser.get()}/${slug}`;
  }

  const langMap = {
    [Languages.EN]: `https://www.${isQA ? 'wthubspot' : 'hubspot'}.com/pricing/${slug}${level ? `/${level}` : ''}`,
    [Languages.DE]: `https://www.${isQA ? 'wthubspot' : 'hubspot'}.de/pricing/${slug}${level ? `/${level}` : ''}`,
    [Languages.ES]: `https://www.${isQA ? 'wthubspot' : 'hubspot'}.es/pricing/${slug}${level ? `/${level}` : ''}`,
    [Languages.PT]: `https://br.${isQA ? 'wthubspot' : 'hubspot'}.com/pricing/${slug}${level ? `/${level}` : ''}`,
    [Languages.FR]: `https://www.${isQA ? 'wthubspot' : 'hubspot'}.fr/pricing/${slug}${level ? `/${level}` : ''}`,
    [Languages.JA]: `https://www.${isQA ? 'wthubspot' : 'hubspot'}.jp/pricing/${slug}${level ? `/${level}` : ''}`
  };
  return langToUrl(langMap);
};
export const getPricingPageProductRoute = isAppAuth => {
  const pathnameSegments = window.location.pathname.split('/');
  let pricingPageProductRoute;

  if (isAppAuth) {
    // in this case the window pathname would look like https://app.hubspot.com/pricing/{portalId}/marketing
    // pricingPageProductRoute will be "marketing"
    pricingPageProductRoute = pathnameSegments[3];
  } else {
    // in this case the window pathname would look like https://www.hubspot.com/pricing/marketing
    // pricingPageProductRoute will be "marketing"
    pricingPageProductRoute = pathnameSegments[2];
  }

  if (AllRoutes.includes(pricingPageProductRoute)) {
    return pricingPageProductRoute;
  }

  return null;
};
export const COMMERCE_WAITLIST_URL = 'https://offers.hubspot.com/stripe-integration-beta-waitlist';